import { AxiosDefaults, AxiosError, AxiosHeaderValue, AxiosInstance, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse, HeadersDefaults, InternalAxiosRequestConfig } from "axios";
import { TickerDelay } from "../../tools/TickerDelay";
import { ILogger } from "../logger/ILogger";

export class HttpClientWrapper {
    private static readonly MAX_ATTEMP_COUNT = 2;
    private static readonly ATTEMPS_DELAY_MS = 500;
    private readonly httpClient: AxiosInstance;
    private readonly logger: ILogger;
    private readonly ignoreStatus: ReadonlyArray<number>;

    public get interceptors(): {
        request: AxiosInterceptorManager<InternalAxiosRequestConfig<any>>;
        response: AxiosInterceptorManager<AxiosResponse<any, any>>;
    } {
        return this.httpClient.interceptors;
    }

    public get defaults(): Omit<AxiosDefaults<any>, "headers"> & {
        headers: HeadersDefaults & {
            [key: string]: AxiosHeaderValue;
        };
    } {
        return this.httpClient.defaults;
    }

    public constructor(httpClient: AxiosInstance, logger: ILogger, ignoreStatus?: Array<number>) {
        this.httpClient = httpClient;
        this.logger = logger;
        this.ignoreStatus = ignoreStatus ?? [];
    }

    public getUri(config?: AxiosRequestConfig): string {
        return this.httpClient.getUri(config);
    }

    public async request<T = any, R = AxiosResponse<T>, D = any>(config: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.request<T, R, D>(config));
    }

    public async get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.get<T, R, D>(url, config));
    }

    public async delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.delete<T, R, D>(url, config));
    }

    public async head<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.head<T, R, D>(url, config));
    }

    public async options<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.options<T, R, D>(url, config));
    }

    public async post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.post<T, R, D>(url, data, config));
    }

    public async put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.put<T, R, D>(url, data, config));
    }

    public async patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.patch<T, R, D>(url, data, config));
    }

    public async postForm<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.postForm<T, R, D>(url, data, config));
    }

    public async putForm<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.putForm<T, R, D>(url, data, config));
    }

    public async patchForm<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
        return this.wrapRequest(() => this.httpClient.patchForm<T, R, D>(url, data, config));
    }

    private async wrapRequest<T = any, R = AxiosResponse<T>>(request: () => Promise<R>, attemp: number = 0): Promise<R> {
        try {
            return await request();
        }
        catch (e: any) {
            this.logger.debug(`Http error caught: ${e?.message ?? e}`);
            if (e instanceof AxiosError) {
                const statusCode = e.response?.status ?? (e.response as any)?.statuscode;
                if (!this.ignoreStatus.includes(statusCode) && attemp < HttpClientWrapper.MAX_ATTEMP_COUNT) {
                    await TickerDelay.delay(HttpClientWrapper.ATTEMPS_DELAY_MS);
                    return this.wrapRequest(request, ++attemp);
                }
            }
            else if (attemp < HttpClientWrapper.MAX_ATTEMP_COUNT){
                await TickerDelay.delay(HttpClientWrapper.ATTEMPS_DELAY_MS);
                return this.wrapRequest(request, ++attemp);
            }
            throw e;
        }
    }
}